import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutlineRounded,
  CreditCardRounded,
  EditNoteRounded,
  Inventory2Outlined,
  PlaceOutlined,
  ReceiptLongRounded,
} from '@mui/icons-material';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import signupFlowSteps from './signupFlowConfig';
import SignupProgress from './signupProgress';
import { signupReviewSchema } from './validationSchemas';
import {
  getAdministrativeLevelMetadata,
  getCountryMetaData,
  subscriptionMetaData,
  processPayment,
  getInitiativesByCountry,
  getIntegratorsByCountry,
} from './login.service';
import { useSignupMetaData } from '../hooks/useSignupMetaData';
import { paymentFlowMode, simulatePaymentAuthorization, handle3DSChallenge } from './paymentProviders';
import { initializeRazorpayCheckout, createRazorpayOrder } from './razorpayProvider';
import { normalizePlan } from './signupPlanCatalog';
import { authHeadingStyles, authSubtextStyles } from './signupTheme';

const fieldLabelStyles = {
  color: 'text.primary',
  fontWeight: 700,
};

const sectionPaperStyles = {
  borderRadius: 2,
  borderColor: '#E4E6F1',
  px: { xs: 2.5, sm: 3 },
  py: { xs: 2.5, sm: 3 },
  backgroundColor: '#ffffff',
  minHeight: { xs: 'auto', md: 252 },
  height: '100%',
};

const summaryRowStyles = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'minmax(120px, 160px) minmax(0, 1fr)' },
  gap: { xs: 0.5, sm: 2 },
  alignItems: 'flex-start',
};

const formatPersonaTypeName = (typeName) => {
  if (!typeName) return typeName;
  const upperTypeName = typeName.toUpperCase();
  if (upperTypeName === 'DARE' || upperTypeName === 'NGO' || upperTypeName === 'FPO') return upperTypeName;
  return typeName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const ecosystemTypeOptions = [
  { id: 'area', label: 'Area' },
  { id: 'initiative', label: 'Initiative' },
  { id: 'crop', label: 'Crop' },
  { id: 'finance', label: 'Finance' },
  { id: 'other', label: 'Other' },
];

const typeKeywordMap = {
  area: ['area', 'region', 'regional', 'zone', 'territory', 'location'],
  initiative: ['initiative', 'program', 'programme', 'project', 'mission'],
  crop: ['crop', 'agri', 'agriculture', 'farm', 'farming', 'seed'],
  finance: ['finance', 'financial', 'credit', 'loan', 'insurance', 'payment'],
  other: ['other', 'general', 'community', 'misc', 'support'],
};

const isDevMockLocationEnabled =
  process.env.NODE_ENV !== 'production' &&
  (process.env.REACT_APP_ENABLE_DEV_LOCATION_MOCKS ?? 'true') === 'true';

const DEV_LOCATION_MOCKS = {
  india: {
    states: [
      { id: 1001, name: 'Karnataka' },
      { id: 1002, name: 'Delhi' },
      { id: 1003, name: 'Maharashtra' },
    ],
    districts: {
      1001: [{ id: 2001, name: 'Bengaluru Urban' }],
      1002: [{ id: 2002, name: 'New Delhi' }],
      1003: [{ id: 2003, name: 'Mumbai' }],
    },
    blocks: {
      2001: [{ id: 3001, name: 'Bengaluru Central' }],
      2002: [{ id: 3002, name: 'Connaught Place' }],
      2003: [{ id: 3003, name: 'Fort' }],
    },
  },
};

const normalizeText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const normalizeCountryKey = (countryName) => normalizeText(countryName);

const getFirstDefinedValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const toArray = (value) => (Array.isArray(value) ? value : []);

const formatCurrency = (currency, amount) =>
  `${currency}${Number(amount || 0).toFixed(2)}`;

const resolveTypeId = (...candidates) => {
  const normalizedCandidates = candidates
    .map((candidate) => normalizeText(candidate))
    .filter(Boolean);

  for (const candidate of normalizedCandidates) {
    const directMatch = ecosystemTypeOptions.find((option) => option.id === candidate);
    if (directMatch) {
      return directMatch.id;
    }

    const labelMatch = ecosystemTypeOptions.find(
      (option) => normalizeText(option.label) === candidate
    );
    if (labelMatch) {
      return labelMatch.id;
    }

    for (const [typeId, keywords] of Object.entries(typeKeywordMap)) {
      const isMatch = keywords.some(
        (keyword) => candidate.includes(keyword) || keyword.includes(candidate)
      );

      if (isMatch) {
        return typeId;
      }
    }
  }

  return null;
};

const normalizeInitiativeEntry = (entry, inheritedType) => {
  const label = getFirstDefinedValue(
    entry?.name,
    entry?.initiativeName,
    entry?.title,
    entry?.label,
    entry?.ecosystemName
  );

  if (!label) {
    return null;
  }

  return {
    id: getFirstDefinedValue(entry?.id, entry?.initiativeId, entry?.value, label),
    label,
    typeId:
      resolveTypeId(
        entry?.type,
        entry?.category,
        entry?.initiativeType,
        entry?.ecosystemType,
        entry?.ecosystemCategory,
        entry?.preferredType,
        inheritedType,
        label
      ) || 'initiative',
  };
};

const normalizeInitiativeMap = (rawEcosystemData) => {
  const initiativeMap = new Map();

  rawEcosystemData.forEach((entry) => {
    const inheritedType = getFirstDefinedValue(
      entry?.name,
      entry?.type,
      entry?.category,
      entry?.ecosystemType
    );
    const nestedItems = getFirstDefinedValue(
      entry?.initiatives,
      entry?.items,
      entry?.children,
      entry?.options,
      entry?.initiativeList
    );

    if (Array.isArray(nestedItems) && nestedItems.length > 0) {
      nestedItems.forEach((nestedEntry) => {
        const normalizedEntry = normalizeInitiativeEntry(nestedEntry, inheritedType);
        if (normalizedEntry) {
          initiativeMap.set(String(normalizedEntry.id), normalizedEntry);
        }
      });
      return;
    }

    const normalizedEntry = normalizeInitiativeEntry(entry, inheritedType);
    if (normalizedEntry) {
      initiativeMap.set(String(normalizedEntry.id), normalizedEntry);
    }
  });

  return initiativeMap;
};

const renderFieldLabel = (label) => (
  <Typography variant="subtitle2" sx={fieldLabelStyles}>
    {label}
  </Typography>
);

const SummaryRow = ({ label, value, strong = false }) => (
  <Box sx={summaryRowStyles}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: strong ? 800 : 700,
        textAlign: { xs: 'left', sm: 'right' },
        color: 'text.primary',
        maxWidth: { xs: '100%', sm: '100%' },
      }}
    >
      {value || '-'}
    </Typography>
  </Box>
);

const getCurrencyCode = (currencySymbol) => {
  const currencyMap = {
    '$': 'USD',
    '€': 'EUR',
    '£': 'GBP',
    '¥': 'JPY',
    '₹': 'INR',
    '₽': 'RUB',
    '¢': 'USD',
  };
  return currencyMap[currencySymbol] || 'USD';
};

export default function SignupStep6({
  handleBack,
  handleChange,
  handleComplete,
  formData,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { userTypes, initiativeEcosystem } = useSignupMetaData(
    formData.administrativeRegion
  );
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [planOptions, setPlanOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [locationOptionsByLevel, setLocationOptionsByLevel] = useState({});
  const [initiativesData, setInitiativesData] = useState([]);
  const [integratorsData, setIntegratorsData] = useState([]);
  const [show3DS, setShow3DS] = useState(false);
  const [clientSecret3DS, setClientSecret3DS] = useState(null);
  const [paymentRequest3DS, setPaymentRequest3DS] = useState(null);
  const [is3DSProcessing, setIs3DSProcessing] = useState(false);
  const has3DSBeenTriggered = React.useRef(false);

  useEffect(() => {
    // Auto-trigger 3DS authentication when modal opens
    if (show3DS && clientSecret3DS && !has3DSBeenTriggered.current) {
      console.log('useEffect: 3DS modal opened, triggering authentication');
      has3DSBeenTriggered.current = true;
      setIs3DSProcessing(true);
      // Call handle3DSConfirmation in next tick to ensure state updates are processed
      setTimeout(() => {
        handle3DSConfirmation();
      }, 100);
    }
  }, [show3DS, clientSecret3DS]);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const response = await subscriptionMetaData(
          formData.userTypeId,
          formData.administrativeRegion
        );

        if (response?.statusCode === 200 || response?.statusCode === 201) {
          const rawPlans = Array.isArray(response?.data)
            ? response.data
            : response?.data?.subscriptionPlans || response?.data?.plans || [];

          if (rawPlans.length > 0) {
            setPlanOptions(rawPlans.map(normalizePlan));
            return;
          }
        }
      } catch (error) {
        // Ignore and fall back to mock plans in development.
      }

      setPlanOptions([]);
    };

    loadPlans();
  }, [formData.administrativeRegion, formData.userTypeId]);

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await getCountryMetaData();
        if (response?.statusCode === 200) {
          setCountryOptions(response.data.map((item) => item.countryManagement));
          return;
        }
      } catch (error) {
        // Ignore and fall back to empty list.
      }

      setCountryOptions([]);
    };

    loadCountries();
  }, []);

  useEffect(() => {
    const loadInitiativesAndIntegrators = async () => {
      if (!formData.administrativeRegion) {
        setInitiativesData([]);
        setIntegratorsData([]);
        return;
      }

      try {
        const [initiativesResponse, integratorsResponse] = await Promise.all([
          getInitiativesByCountry(formData.administrativeRegion),
          getIntegratorsByCountry(formData.administrativeRegion),
        ]);

        if (initiativesResponse?.statusCode === 200) {
          setInitiativesData(initiativesResponse.data || []);
        }
        if (integratorsResponse?.statusCode === 200) {
          setIntegratorsData(integratorsResponse.data || []);
        }
      } catch (error) {
        // Ignore and fall back to empty lists
      }
    };

    loadInitiativesAndIntegrators();
  }, [formData.administrativeRegion]);

  const selectedCountry = useMemo(
    () =>
      countryOptions.find(
        (country) => String(country.id) === String(formData.administrativeRegion)
      ) || null,
    [countryOptions, formData.administrativeRegion]
  );

  const devLocationConfig = useMemo(
    () => DEV_LOCATION_MOCKS[normalizeCountryKey(selectedCountry?.name)] || null,
    [selectedCountry]
  );

  const getPostalCodeLabel = () => {
    if (formData.addressTemplate?.postalCode?.name) {
      return formData.addressTemplate.postalCode.name;
    }
    return 'Postal Code';
  };

  const getLevelFieldName = (levelNumber) => {
    // Use generic field names based on level number
    return `level${levelNumber}`;
  };

  useEffect(() => {
    const loadLocationLevels = async () => {
      if (!formData.administrativeRegion || !formData.addressTemplate?.levels) {
        setLocationOptionsByLevel({});
        return;
      }

      const levels = formData.addressTemplate.levels;
      const newLocationOptions = { ...locationOptionsByLevel };

      for (const level of levels) {
        const levelNum = level.level;

        // Get parent level ID if needed
        let parentId = null;
        if (levelNum > 1) {
          const parentLevel = levels.find((l) => l.level === levelNum - 1);
          if (parentLevel) {
            const parentFieldName = getLevelFieldName(parentLevel.level);
            parentId = formData[parentFieldName];
            if (!parentId) {
              newLocationOptions[levelNum] = [];
              continue;
            }
          }
        }

        // Try dev mock data first
        if (isDevMockLocationEnabled && devLocationConfig) {
          if (levelNum === 1) {
            newLocationOptions[levelNum] = devLocationConfig.states || [];
          } else if (levelNum === 2) {
            newLocationOptions[levelNum] =
              devLocationConfig.districts[Number(parentId)] || [];
          } else if (levelNum === 3) {
            newLocationOptions[levelNum] =
              devLocationConfig.blocks[Number(parentId)] || [];
          }
          continue;
        }

        // Fetch from backend
        try {
          const response = await getAdministrativeLevelMetadata(
            levelNum,
            formData.administrativeRegion,
            parentId || null
          );
          if (response?.statusCode === 200) {
            newLocationOptions[levelNum] = response.data || [];
          } else {
            newLocationOptions[levelNum] = [];
          }
        } catch (error) {
          newLocationOptions[levelNum] = [];
        }
      }

      setLocationOptionsByLevel(newLocationOptions);
    };

    loadLocationLevels();
  }, [
    devLocationConfig,
    formData.administrativeRegion,
    formData.addressTemplate?.levels,
  ]);

  const selectedUserType = useMemo(
    () =>
      userTypes.find((userType) => String(userType.id) === String(formData.userTypeId)) ||
      null,
    [formData.userTypeId, userTypes]
  );

  const selectedPlan = useMemo(
    () =>
      planOptions.find(
        (plan) => String(plan.id) === String(formData.subscriptionPlanId)
      ) || null,
    [formData.subscriptionPlanId, planOptions]
  );

  const countryLabel = useMemo(
    () => selectedCountry?.name || '',
    [selectedCountry]
  );

  const initiativeMap = useMemo(
    () => normalizeInitiativeMap(toArray(initiativeEcosystem)),
    [initiativeEcosystem]
  );

  const selectedInitiativeLabels = useMemo(
    () =>
      toArray(formData.initiativeEcosystemToJoin).map((initiativeId) => {
        const matchedInitiative = initiativeMap.get(String(initiativeId));
        if (matchedInitiative?.label) {
          return matchedInitiative.label;
        }
        // Fallback: search in initiativesData
        const initiativeFromData = initiativesData.find(
          (item) => String(item.id) === String(initiativeId)
        );
        return initiativeFromData?.name || initiativeFromData?.initiativeName || String(initiativeId);
      }),
    [formData.initiativeEcosystemToJoin, initiativeMap, initiativesData]
  );

  const selectedTypeLabels = useMemo(
    () =>
      toArray(formData.preferredEcosystemTypes).map(
        (typeId) =>
          ecosystemTypeOptions.find((option) => option.id === typeId)?.label || typeId
      ),
    [formData.preferredEcosystemTypes]
  );

  const summary = useMemo(() => {
    const currency =
      selectedPlan?.displayCurrency || formData.subscriptionPlanCurrency || '$';
    const currencyCode =
      selectedPlan?.currencyCode || formData.subscriptionPlanCurrencyCode || 'USD';

    const monthlySubscriptionPrice = selectedPlan?.monthlyPrice ?? 0;
    const yearlySubscriptionPrice = selectedPlan?.yearlyPrice ?? selectedPlan?.monthlyPrice ?? 0;

    // For monthly billing with financial support, calculate 12 months (upfront payment)
    // For monthly billing without financial support, calculate 1 month (recurring)
    // For yearly, use yearly price
    const subscriptionPrice =
      formData.subscriptionBillingCycle === 'yearly'
        ? yearlySubscriptionPrice
        : formData.financialSupportRequested
        ? monthlySubscriptionPrice * 12
        : monthlySubscriptionPrice;

    const setupFee =
      formData.subscriptionBillingCycle === 'yearly'
        ? selectedPlan?.yearlySetupFee ?? selectedPlan?.setupFee ?? 0
        : selectedPlan?.setupFee ?? 0;
    const subtotal = Number(subscriptionPrice) + Number(setupFee);

    // Apply financial support coverage first (if applicable)
    let coverageAmount = 0;
    let amountAfterCoverage = subtotal;

    if (formData.financialSupportRequested && formData.financialSupportCoveragePercentage) {
      coverageAmount = (subtotal * formData.financialSupportCoveragePercentage) / 100;
      amountAfterCoverage = subtotal - coverageAmount;
    }

    // Then apply promo discount to the coverage-adjusted amount
    const discountPercentage = Number(formData.promoDiscountAmount || 0);
    const discountAmount = (amountAfterCoverage * discountPercentage) / 100;
    const total = Math.max(0, amountAfterCoverage - discountAmount);

    return {
      currency,
      currencyCode,
      subscriptionPrice,
      setupFee,
      discountAmount,
      subtotal,
      coverageAmount,
      amountAfterCoverage,
      total,
      totalFormatted: formatCurrency(currency, total),
    };
  }, [
    formData.promoDiscountAmount,
    formData.subscriptionBillingCycle,
    formData.subscriptionPlanCurrency,
    formData.subscriptionPlanCurrencyCode,
    formData.financialSupportRequested,
    formData.financialSupportCoveragePercentage,
    selectedPlan,
  ]);

  const accountName =
    formData.registrationType === 'organization'
      ? formData.organisationName
      : [formData.firstName, formData.lastName].filter(Boolean).join(' ');

  const toSentenceCase = (text) =>
    text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : '';

  const registrationTypeLabel = toSentenceCase(formData.registrationType);

  const getAddressLevelValue = (levelNumber) => {
    const fieldName = getLevelFieldName(levelNumber);
    const fieldValue = formData[fieldName];

    if (!fieldValue) return '';

    // First, check if we have a stored name (from postal code lookup)
    const storedName = formData[`${fieldName}Name`];
    if (storedName) return storedName;

    // Otherwise, get the options for this level and find the matching name
    const options = locationOptionsByLevel[levelNumber] || [];
    const option = options.find((opt) => String(opt.id) === String(fieldValue));
    return option?.name || fieldValue;
  };

  const addressLevelsToDisplay = useMemo(
    () => formData.addressTemplate?.levels || [],
    [formData.addressTemplate?.levels]
  );

  const selectedIntegratorLabels = useMemo(
    () =>
      toArray(formData.selectedIntegrators).map((integratorId) => {
        const integrator = integratorsData.find(
          (item) => String(item.id) === String(integratorId)
        );
        return integrator?.name || integrator?.integratorName || String(integratorId);
      }),
    [formData.selectedIntegrators, integratorsData]
  );

  // paymentMethodType is the provider (stripe, razorpay, apple_pay, google_pay, cash, etc.)
  const paymentMethodType =
    formData.preferredPaymentProvider || formData.preferredPaymentMethodType || 'card';

  // actualMethodType is the actual payment method type (card, wallet, cash, etc.)
  const actualMethodType = formData.preferredPaymentMethodType || 'card';

  const paymentMethodLabel =
    formData.preferredPaymentMethodName ||
    (paymentMethodType === 'apple_pay'
      ? 'Apple Pay'
      : paymentMethodType === 'google_pay'
        ? 'Google Pay'
        : paymentMethodType === 'cash'
          ? 'Cash'
          : 'Debit/Credit Card');

  const maskedCardLabel =
    paymentMethodType === 'card' && formData.paymentCardNumber
      ? `Card ending in ${String(formData.paymentCardNumber).slice(-4)}`
      : '';

  const handleReviewConfirmationChange = async (event) => {
    handleChange(event);

    try {
      const updatedFormData = {
        ...formData,
        reviewConfirmed: event.target.checked,
      };
      await signupReviewSchema.validateAt('reviewConfirmed', updatedFormData);
      setErrors({});
    } catch (error) {
      setErrors({ reviewConfirmed: error.message });
    }
  };

  const handle3DSConfirmation = async () => {
    console.log('3DS confirmation started');

    try {
      if (!stripe || !clientSecret3DS || !paymentRequest3DS) {
        console.error('3DS setup incomplete:', { stripe: !!stripe, clientSecret: !!clientSecret3DS, paymentRequest: !!paymentRequest3DS });
        setPaymentError('3D Secure setup error. Please try again.');
        has3DSBeenTriggered.current = false;
        setShow3DS(false);
        setIs3DSProcessing(false);
        return;
      }

      setPaymentError('');
      console.log('Initiating 3DS challenge with Stripe...');

      // Handle the 3D Secure challenge
      // This will show the bank's authentication modal to the user
      const threeDSResult = await handle3DSChallenge(stripe, clientSecret3DS);
      console.log('3DS challenge result:', { ok: threeDSResult.ok, status: threeDSResult.status });

      if (!threeDSResult.ok) {
        console.error('3DS authentication failed:', threeDSResult.message);
        setPaymentError(threeDSResult.message || '3D Secure authentication failed');
        has3DSBeenTriggered.current = false;
        setIs3DSProcessing(false);
        setShow3DS(false); // Close the 3DS modal and show error on main screen
        return;
      }

      // Call confirm3DS endpoint with the PaymentIntent ID (not client secret)
      console.log('User authenticated with bank. Confirming payment with backend...');
      console.log('3DS Request details:', {
        hasClientSecret: !!clientSecret3DS,
        hasTransactionId: !!paymentRequest3DS?.transactionId,
      });

      const confirm3DSRequest = {
        // Backend expects paymentIntentId (which is stored as transactionId in our paymentRequest3DS)
        paymentIntentId: paymentRequest3DS.transactionId,
        clientSecret: clientSecret3DS,
        userTypeId: paymentRequest3DS.userTypeId,
        subscriptionPlanId: paymentRequest3DS.subscriptionPlanId,
        membershipId: paymentRequest3DS.membershipId,
        promoCode: paymentRequest3DS.promoCode,
        paymentMethodType: 'stripe', // For 3DS, it's always card-based
      };

      console.log('Sending confirm3DS request:', JSON.stringify({
        paymentIntentId: confirm3DSRequest.paymentIntentId?.substring(0, 20) + '...',
        hasClientSecret: !!confirm3DSRequest.clientSecret,
        userTypeId: confirm3DSRequest.userTypeId,
        subscriptionPlanId: confirm3DSRequest.subscriptionPlanId,
      }, null, 2));

      // Import and call confirm3DS service
      const { confirm3DSPayment } = await import('./login.service');
      const confirm3DSResponse = await confirm3DSPayment(confirm3DSRequest);
      console.log('Backend confirm3DS response:', { statusCode: confirm3DSResponse?.statusCode, paymentStatus: confirm3DSResponse?.paymentStatus });

      if (!confirm3DSResponse || confirm3DSResponse.statusCode !== 200) {
        console.error('Backend confirmation failed:', confirm3DSResponse?.message);
        setPaymentError(
          confirm3DSResponse?.message || 'Payment confirmation failed. Please try again.'
        );
        has3DSBeenTriggered.current = false;
        setIs3DSProcessing(false);
        return;
      }

      console.log('Payment confirmed. Completing enrollment...');
      // Payment successful, close 3DS modal and proceed
      has3DSBeenTriggered.current = false;
      setShow3DS(false);
      setClientSecret3DS(null);
      setPaymentRequest3DS(null);
      setIs3DSProcessing(false);

      // Proceed with signup completion
      const selectedPlan = planOptions.find((p) => p.id === formData.subscriptionPlanId);
      const paymentResult = {
        ok: true,
        status: confirm3DSResponse.paymentStatus,
        statusLabel: 'Payment Completed',
        transactionId: confirm3DSResponse.transactionId,
        receiptLabel: confirm3DSResponse.paymentId,
        message: 'Payment processed successfully',
      };

      console.log('Calling handleComplete with payment result:', paymentResult);
      await handleComplete({
        planName: selectedPlan?.planName || formData.subscriptionPlanName || 'Selected Plan',
        billingCycle: formData.subscriptionBillingCycle || 'monthly',
        paymentMethodName: 'Debit/Credit Card',
        paymentMethodType: 'stripe',
        totalFormatted: summary.totalFormatted,
        financialSupportRequested: Boolean(formData.financialSupportRequested),
        paymentStatus: paymentResult.status,
        paymentStatusLabel: paymentResult.statusLabel,
        transactionId: paymentResult.transactionId,
        paymentReceiptLabel: paymentResult.receiptLabel,
        paymentMessage: paymentResult.message,
        paymentFlowMode,
      });
      console.log('Enrollment completed after 3DS payment');
    } catch (error) {
      console.error('Exception in 3DS confirmation:', error);
      setPaymentError('An error occurred during payment confirmation. Please try again.');
      has3DSBeenTriggered.current = false;
      setIs3DSProcessing(false);
    }
  };

  const handleCompleteEnrollment = async (event) => {
    console.log('handleCompleteEnrollment called');
    event.preventDefault();
    setPaymentError('');

    try {
      await signupReviewSchema.validate(
        {
          reviewConfirmed: formData.reviewConfirmed,
        },
        { abortEarly: false }
      );
      setErrors({});
    } catch (validationError) {
      console.log('Validation failed:', validationError);
      const nextErrors = {};
      validationError.inner.forEach((error) => {
        if (!nextErrors[error.path]) {
          nextErrors[error.path] = error.message;
        }
      });
      setErrors(nextErrors);
      return;
    }

    console.log('Validation passed, starting payment processing');
    console.log('Payment method type:', paymentMethodType);
    console.log('Has stripePaymentMethodId:', !!formData.stripePaymentMethodId);
    console.log('Has clientSecret:', !!formData.clientSecret);
    setSubmitting(true);

    try {
      let paymentResult = null;

      // Handle payment based on method type and available tokens

      // 1. Mock simulation for wallet methods in mock mode
      if ((paymentMethodType === 'apple_pay' || paymentMethodType === 'google_pay') && paymentFlowMode === 'mock') {
        console.log(`🔄 Mock mode: simulating ${paymentMethodType} payment`);
        const simulationResult = await simulatePaymentAuthorization({
          provider: paymentMethodType,
          amountFormatted: summary.totalFormatted,
        });

        if (!simulationResult.ok) {
          setPaymentError(simulationResult.message || 'We could not authorize this payment yet.');
          setSubmitting(false);
          return;
        }

        paymentResult = simulationResult;
        console.log(`✅ Mock ${paymentMethodType} payment authorized:`, paymentResult.status);
      }
      // 2. Stripe Payment Intent flow (wallet payments with clientSecret from Step 5)
      else if (formData.clientSecret && !stripe) {
        setPaymentError('Stripe is not initialized. Please refresh and try again.');
        setSubmitting(false);
        return;
      } else if (formData.clientSecret && (paymentMethodType === 'google_pay' || paymentMethodType === 'apple_pay' || paymentMethodType === 'wallet')) {
        if (!stripe) {
          setPaymentError('Stripe is not initialized. Please refresh and try again.');
          setSubmitting(false);
          return;
        }

        const subscriptionPlanId = String(formData.subscriptionPlanId || '').trim();
        if (!subscriptionPlanId) {
          setPaymentError('Subscription plan is required.');
          setSubmitting(false);
          return;
        }

        // Wallet Payment Flow using Payment Request API (Apple Pay, Google Pay)
        console.log('🔄 Processing wallet payment with Payment Request API...');
        console.log('Wallet Method Type:', paymentMethodType);

        // Create Payment Request configuration
        const paymentRequestConfig = {
          country: 'US',
          currency: summary.currencyCode.toLowerCase(),
          total: {
            label: 'eKutir Subscription',
            amount: Math.round(summary.total * 100), // Amount in cents
          },
          requestPayerEmail: true,
          requestPayerName: true,
          requestPayerPhone: true,
          displayItems: [
            {
              label: selectedPlan?.planName || formData.subscriptionPlanName || 'Subscription',
              amount: Math.round((formData.subscriptionBillingCycle === 'yearly' ? summary.subscriptionPrice : summary.subscriptionPrice) * 100),
            }
          ]
        };

        console.log('Creating Payment Request with:', {
          country: paymentRequestConfig.country,
          currency: paymentRequestConfig.currency,
          amount: summary.total,
          amountInCents: paymentRequestConfig.total.amount
        });

        // Create payment request
        const paymentRequest = stripe.paymentRequest(paymentRequestConfig);

        console.log('💳 Payment Request created. Checking payment availability...');

        // IMPORTANT: Must check canMakePayment() on the SAME object before calling show()
        // This is required by Stripe and the Payment Request API
        let canMakePayment;
        try {
          canMakePayment = await paymentRequest.canMakePayment();
          console.log('💳 canMakePayment result:', {
            canMakePayment,
            type: typeof canMakePayment
          });
        } catch (canMakePaymentError) {
          console.error('🔴 Error checking canMakePayment:', {
            message: canMakePaymentError?.message,
            error: canMakePaymentError
          });
          setPaymentError('Unable to check payment availability. Please try again.');
          setSubmitting(false);
          return;
        }

        if (!canMakePayment) {
          console.error('🔴 Payment Request API not available on this device');
          setPaymentError('Apple Pay or Google Pay is not available on your device. Please use a debit/credit card instead.');
          setSubmitting(false);
          return;
        }

        console.log('✅ Payment Request API available. Showing payment sheet now...');
        console.log('Payment Request object:', {
          hasPaymentRequest: !!paymentRequest,
          type: typeof paymentRequest,
          hasShow: !!paymentRequest?.show
        });

        // Show the payment sheet (Apple Pay / Google Pay modal)
        let walletPaymentMethod;
        let walletPaymentError;

        try {
          console.log('📱 Calling paymentRequest.show()...');
          const startTime = Date.now();
          const result = await paymentRequest.show();
          const elapsedTime = Date.now() - startTime;

          console.log('🔍 paymentRequest.show() returned after', elapsedTime, 'ms:', {
            result: result,
            isNull: result === null,
            isUndefined: result === undefined,
            isEmpty: result && Object.keys(result || {}).length === 0,
            type: typeof result,
            keys: result ? Object.keys(result) : 'N/A',
            constructorName: result?.constructor?.name
          });

          // If result is null/undefined, check if it's because the sheet was dismissed
          if (!result) {
            console.warn('⚠️ Payment request returned null/undefined');
            console.warn('Possible causes:');
            console.warn('1. User dismissed the Google Pay sheet');
            console.warn('2. Google Pay timed out or encountered an error');
            console.warn('3. Payment sheet was closed unexpectedly');
            console.warn('4. Device/browser compatibility issue');
            setPaymentError(
              'Google Pay sheet was dismissed or encountered an error. Please ensure you completed the transaction in the payment sheet. Try again.'
            );
            setSubmitting(false);
            return;
          }

          // Check for empty result object
          if (Object.keys(result).length === 0) {
            console.error('🔴 Payment request returned empty object');
            setPaymentError('Payment sheet did not provide payment method. Please try again.');
            setSubmitting(false);
            return;
          }

          // Extract payment method from different possible locations
          walletPaymentMethod = result.paymentMethod || result.methodName || result.method;
          walletPaymentError = result.error;

          console.log('💳 Extracted values:', {
            hasPaymentMethod: !!walletPaymentMethod,
            hasError: !!walletPaymentError,
            paymentMethodId: walletPaymentMethod?.id,
            paymentMethodType: walletPaymentMethod?.type,
            fullPaymentMethod: JSON.stringify(walletPaymentMethod)
          });

          // Additional validation
          if (walletPaymentMethod && !walletPaymentMethod.id) {
            console.warn('⚠️ Payment method exists but has no ID');
            console.warn('Payment method object:', walletPaymentMethod);
          }
        } catch (paymentRequestError) {
          console.error('🔴 Payment request exception:', {
            message: paymentRequestError?.message,
            name: paymentRequestError?.name,
            code: paymentRequestError?.code,
            toString: paymentRequestError?.toString?.()
          });

          // Check if it's a specific error type
          if (paymentRequestError?.message?.includes('dismissed') || paymentRequestError?.message?.includes('cancelled')) {
            setPaymentError('Payment was cancelled. Please try again.');
          } else {
            setPaymentError(paymentRequestError?.message || 'Payment request failed. Please try again.');
          }
          setSubmitting(false);
          return;
        }

        if (walletPaymentError) {
          console.error('🔴 Payment request failed:', walletPaymentError);
          setPaymentError(walletPaymentError.message || 'Payment cancelled. Please try again.');
          setSubmitting(false);
          return;
        }

        if (!walletPaymentMethod) {
          console.error('🔴 No payment method returned from payment sheet');
          console.warn('This usually means the payment sheet was cancelled or closed unexpectedly');
          setPaymentError('No payment method was selected. Please try again.');
          setSubmitting(false);
          return;
        }

        console.log('✅ Payment sheet authorized wallet payment. Confirming with Stripe...');

        // Safely get payment method ID
        const paymentMethodId = walletPaymentMethod?.id;
        if (!paymentMethodId) {
          console.error('🔴 No payment method ID available');
          console.error('Wallet Payment Method details:', {
            type: typeof walletPaymentMethod,
            hasId: !!walletPaymentMethod?.id,
            keys: walletPaymentMethod ? Object.keys(walletPaymentMethod) : 'N/A',
            fullObject: JSON.stringify(walletPaymentMethod)
          });
          setPaymentError('Payment method could not be processed. Please try again.');
          setSubmitting(false);
          return;
        }

        console.log('💳 Using payment method ID:', paymentMethodId?.substring(0, 20) + '...');
        console.log('📲 Confirming wallet payment with Stripe...');

        let confirmResult;
        try {
          // Confirm the PaymentIntent with the payment method from the sheet
          confirmResult = await stripe.confirmPayment({
            clientSecret: formData.clientSecret,
            payment_method: paymentMethodId,
            redirect: 'if_required',
          });

          console.log('✅ confirmPayment completed:', {
            hasError: !!confirmResult?.error,
            errorMessage: confirmResult?.error?.message,
            hasPaymentIntent: !!confirmResult?.paymentIntent,
            paymentIntentStatus: confirmResult?.paymentIntent?.status
          });
        } catch (confirmError) {
          console.error('❌ Exception during confirmPayment:', {
            message: confirmError?.message,
            error: confirmError
          });
          setPaymentError(confirmError?.message || 'Payment confirmation failed. Please try again.');
          setSubmitting(false);
          return;
        }

        if (!confirmResult) {
          console.error('🔴 confirmPayment returned null/undefined');
          setPaymentError('Payment could not be confirmed. Please try again.');
          setSubmitting(false);
          return;
        }

        if (confirmResult.error) {
          console.error('Payment confirmation error:', confirmResult.error);
          setPaymentError(confirmResult.error.message || 'Payment failed. Please try again.');
          setSubmitting(false);
          return;
        }

        const { paymentIntent } = confirmResult;
        if (!paymentIntent) {
          console.error('No paymentIntent in confirmation result');
          setPaymentError('Payment intent was not confirmed. Please try again.');
          setSubmitting(false);
          return;
        }

        console.log('💰 PaymentIntent status:', paymentIntent.status);

        if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing') {
          console.log('✅ Wallet payment confirmed successfully');

          // Confirm with backend to finalize payment record
          const confirm3DSRequest = {
            clientSecret: formData.clientSecret,
            userTypeId: formData.userTypeId,
            subscriptionPlanId: formData.subscriptionPlanId,
            membershipId: null,
            promoCode: formData.promoCode || null,
            paymentMethodType: paymentMethodType,
          };

          try {
            const { confirm3DSPayment } = await import('./login.service');
            const confirm3DSResponse = await confirm3DSPayment(confirm3DSRequest);
            console.log('Backend confirmation response:', confirm3DSResponse);

            if (confirm3DSResponse?.statusCode === 200) {
              paymentResult = {
                ok: true,
                status: confirm3DSResponse.paymentStatus || paymentIntent.status,
                statusLabel: 'Payment Completed',
                transactionId: confirm3DSResponse.transactionId || paymentIntent.id,
                receiptLabel: confirm3DSResponse.paymentId,
                message: 'Payment processed successfully',
              };
              console.log('✅ Backend confirmation successful');
            } else {
              console.error('Backend confirmation failed:', confirm3DSResponse?.message);
              setPaymentError(confirm3DSResponse?.message || 'Payment backend confirmation failed. Please try again.');
              setSubmitting(false);
              return;
            }
          } catch (error) {
            console.error('Error confirming with backend:', error);
            setPaymentError('Error processing payment. Please try again.');
            setSubmitting(false);
            return;
          }
        } else if (paymentIntent.status === 'requires_action') {
          console.warn('PaymentIntent requires additional action');
          setPaymentError('Payment requires additional verification. Please try again.');
          setSubmitting(false);
          return;
        } else {
          console.error('Unexpected PaymentIntent status:', paymentIntent.status);
          setPaymentError(`Payment status: "${paymentIntent.status}". Please contact support.`);
          setSubmitting(false);
          return;
        }
      } else if (actualMethodType === 'card' && selectedCountry?.name?.toLowerCase() === 'india') {
        // Razorpay flow for India card payments
        console.log('🔄 Processing Razorpay card payment for India');

        // Check if Razorpay is configured
        if (!process.env.REACT_APP_RAZORPAY_KEY_ID) {
          console.error('❌ REACT_APP_RAZORPAY_KEY_ID is not set');
          setPaymentError('Razorpay is not configured. Please contact support.');
          setSubmitting(false);
          return;
        }

        const subscriptionPlanId = String(formData.subscriptionPlanId || '').trim();
        if (!subscriptionPlanId) {
          setPaymentError('Subscription plan is required.');
          setSubmitting(false);
          return;
        }

        try {
          console.log('📊 Creating Razorpay order with:', {
            amount: summary.total,
            currency: summary.currencyCode,
            userTypeId: formData.userTypeId,
            subscriptionPlanId,
            promoCode: formData.promoCode,
            discountAmount: summary.discountAmount,
          });

          // Create Razorpay order
          const fullName = formData.registrationType === 'organization'
            ? formData.organisationName
            : [formData.firstName, formData.lastName].filter(Boolean).join(' ');

          const orderResponse = await createRazorpayOrder(
            process.env.REACT_APP_API_URL || 'http://localhost:8180',
            summary.total,
            summary.currencyCode,
            formData.userTypeId,
            subscriptionPlanId,
            formData.email,
            fullName,
            formData.phoneNumber,
            formData.promoCode,
            formData.subscriptionBillingCycle,
            formData.financialSupportRequested ? formData.financialSupportPlanId : null
          );

          console.log('✅ Razorpay order created:', orderResponse);

          if (!orderResponse?.orderId) {
            setPaymentError('Failed to create payment order. Please try again.');
            setSubmitting(false);
            return;
          }

          console.log('📱 Opening Razorpay checkout modal...');

          // Open Razorpay checkout and wait for result
          const razorpayResult = await initializeRazorpayCheckout(
            process.env.REACT_APP_RAZORPAY_KEY_ID,
            orderResponse,
            summary.total,
            summary.currencyCode,
            formData.email,
            formData.fullName,
            formData.phoneNumber,
            (successResult) => {
              console.log('✅ Razorpay payment completed successfully:', successResult);
            },
            (error) => {
              console.error('❌ Razorpay payment error:', error);
            }
          );

          // Payment was verified, set result
          paymentResult = {
            ok: true,
            status: razorpayResult.status,
            statusLabel: razorpayResult.statusLabel,
            transactionId: razorpayResult.transactionId,
            orderId: razorpayResult.orderId,
            message: razorpayResult.message,
          };
          console.log('✅ Razorpay payment verified and processing enrollment');
        } catch (error) {
          console.error('❌ Razorpay error:', {
            message: error?.message,
            name: error?.name,
            stack: error?.stack,
          });
          setPaymentError(error?.message || 'Razorpay payment failed. Please try again.');
          setSubmitting(false);
          return;
        }
      } else if (paymentMethodType === 'stripe' && formData.stripePaymentMethodId) {
        // Card payment flow: Use stripePaymentMethodId from Step 5 CardElement tokenization
        console.log('🔄 Processing card payment with stripePaymentMethodId from Step 5');

        const subscriptionPlanId = String(formData.subscriptionPlanId || '').trim();
        if (!subscriptionPlanId) {
          setPaymentError('Subscription plan is required.');
          setSubmitting(false);
          return;
        }

        const paymentRequest = {
          amount: summary.total,
          currency: summary.currencyCode,
          userTypeId: formData.userTypeId,
          subscriptionPlanId: subscriptionPlanId,
          membershipId: null,
          promoCode: formData.promoCode || null,
          paymentMethodType: paymentMethodType,
          stripeToken: formData.stripePaymentMethodId,
        };

        const paymentResponse = await processPayment(paymentRequest);
        console.log('Card payment response:', { statusCode: paymentResponse?.statusCode });

        if (paymentResponse?.statusCode === 400) {
          setPaymentError(paymentResponse?.message || 'Payment request was rejected. Please try again.');
          setSubmitting(false);
          return;
        }

        // If 3DS required, confirm with Stripe
        if (paymentResponse?.clientSecret && stripe) {
          console.log('✅ 3DS required - confirming card payment with Stripe');
          const confirmResult = await stripe.confirmPayment({
            clientSecret: paymentResponse.clientSecret,
            redirect: 'if_required',
          });

          if (confirmResult.error) {
            setPaymentError(confirmResult.error.message || 'Card payment failed. Please try again.');
            setSubmitting(false);
            return;
          }

          const { paymentIntent } = confirmResult;
          if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
            paymentResult = {
              ok: true,
              status: paymentIntent.status,
              statusLabel: 'Payment Completed',
              transactionId: paymentResponse.transactionId || paymentIntent.id,
              receiptLabel: paymentResponse.paymentId || paymentIntent.id,
              message: 'Card payment processed successfully',
            };
            console.log('✅ Card payment confirmed via 3DS');
          } else {
            setPaymentError(`Card payment failed: ${paymentIntent?.status || 'unknown error'}`);
            setSubmitting(false);
            return;
          }
        } else if (paymentResponse?.statusCode === 200) {
          // Payment succeeded without 3DS
          paymentResult = {
            ok: true,
            status: 'succeeded',
            statusLabel: 'Payment Completed',
            transactionId: paymentResponse.transactionId,
            receiptLabel: paymentResponse.paymentId,
            message: 'Card payment processed successfully',
          };
          console.log('✅ Card payment succeeded');
        } else {
          setPaymentError(paymentResponse?.message || 'Card payment failed. Please try again.');
          setSubmitting(false);
          return;
        }
      } else {
        // A live build never simulates an online payment. Without this, pressing Complete Enrollment
        // before the payment method and country had loaded fell through to the simulation and
        // created the account with no payment. Cash and bank transfer are offline and still go on.
        const isOfflineMethod = ['cash', 'bank_transfer', 'bank'].includes(paymentMethodType)
          || ['cash', 'bank'].includes(actualMethodType);
        if (paymentFlowMode === 'live' && !isOfflineMethod) {
          setPaymentError('The payment could not be started. Please wait a moment and try again.');
          setSubmitting(false);
          return;
        }
        // For non-Stripe methods, use mock simulation
        console.log('🔄 Using mock simulation for payment method:', paymentMethodType);
        const simulationResult = await simulatePaymentAuthorization({
          provider: paymentMethodType,
          amountFormatted: summary.totalFormatted,
          cardNumber: formData.paymentCardNumber,
        });

        if (!simulationResult.ok) {
          setPaymentError(simulationResult.message || 'We could not authorize this payment yet.');
          setSubmitting(false);
          return;
        }

        paymentResult = simulationResult;
        console.log('✅ Mock payment authorized:', paymentResult.status);
      }

      // Verify paymentResult exists before completing enrollment
      if (!paymentResult) {
        setPaymentError('Payment verification failed. Please try again.');
        setSubmitting(false);
        return;
      }

      console.log('Payment completed successfully. Completing enrollment...');
      await handleComplete({
        planName: selectedPlan?.planName || formData.subscriptionPlanName || 'Selected Plan',
        billingCycle: formData.subscriptionBillingCycle || 'monthly',
        paymentMethodName: paymentMethodLabel,
        paymentMethodType,
        totalFormatted: summary.totalFormatted,
        financialSupportRequested: Boolean(formData.financialSupportRequested),
        paymentStatus: paymentResult.status,
        paymentStatusLabel: paymentResult.statusLabel,
        transactionId: paymentResult.transactionId,
        orderId: paymentResult.orderId,
        paymentReceiptLabel: paymentResult.receiptLabel,
        paymentMessage: paymentResult.message,
        paymentFlowMode,
      });
      console.log('Enrollment completed successfully');
    } catch (error) {
      console.error('Unexpected error in handleCompleteEnrollment:', error);
      setPaymentError(
        error?.message || 'An unexpected error occurred. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={4} sx={{ maxWidth: 1120, mx: 'auto' }}>
      <Box sx={{ width: '100%', px: { xs: 0, md: 1 }, pb: 1 }}>
        <SignupProgress steps={signupFlowSteps} activeStep={5} />
      </Box>

      <Paper
        elevation={0}
        sx={{
          px: { xs: 2.5, sm: 6, md: 8 },
          py: { xs: 2.5, sm: 6, md: 8 },
          borderRadius: 4,
          backgroundColor: '#ffffff',
          boxShadow: '0 18px 48px rgba(28, 27, 31, 0.10)',
          border: '1px solid #E4E6F1',
        }}
      >
          <Stack spacing={4}>
            <Stack spacing={1} alignItems="center" textAlign="center">
              <Typography
                variant="h4"
                sx={authHeadingStyles}
              >
                {formData.isCustomPlanRequest ? 'Review & Submit Request' : 'Review & Confirm Subscription'}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  ...authSubtextStyles,
                  maxWidth: { xs: '34ch', sm: '48ch', md: '56ch' },
                }}
              >
                {formData.isCustomPlanRequest
                  ? 'Review your information and submit your custom plan request. Our team will contact you soon.'
                  : 'Review the information you entered, confirm your subscription choice, and enroll when everything looks correct.'}
              </Typography>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
              gap: { xs: 2.5, lg: 3 },
              alignItems: 'start',
              minHeight: { xs: 'auto', md: 550 },
            }}
          >
            <Paper variant="outlined" sx={sectionPaperStyles}>
              <Stack spacing={2} sx={{ height: '100%' }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <EditNoteRounded color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Account Details
                  </Typography>
                </Stack>
                <SummaryRow label="Registration Type" value={registrationTypeLabel} />
                <SummaryRow label="Name" value={accountName} />
                <SummaryRow
                  label="Role"
                  value={selectedUserType?.typeName || formData.userTypeId}
                />
                {formData.serviceProviderType ? (
                  <SummaryRow
                    label="Service Provider Type"
                    value={formData.serviceProviderType}
                  />
                ) : null}
                <SummaryRow label="Email" value={formData.email} />
                <SummaryRow
                  label="Phone"
                  value={`${formData.countryCode || ''} ${formData.phoneNumber || ''}`}
                />
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={sectionPaperStyles}>
              <Stack spacing={2} sx={{ height: '100%' }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <PlaceOutlined color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Address Details
                  </Typography>
                </Stack>
                <SummaryRow label={getPostalCodeLabel()} value={formData.pincode} />
                <SummaryRow label="Country" value={countryLabel} />
                {addressLevelsToDisplay.length > 0 ? (
                  addressLevelsToDisplay.map((level) => (
                    <SummaryRow
                      key={`level-${level.level}`}
                      label={level.name}
                      value={getAddressLevelValue(level.level)}
                    />
                  ))
                ) : null}
                <SummaryRow label="Address Line 1" value={formData.addressLine1} />
                {formData.addressLine2 ? (
                  <SummaryRow label="Address Line 2" value={formData.addressLine2} />
                ) : null}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={sectionPaperStyles}>
              <Stack spacing={2} sx={{ height: '100%' }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Inventory2Outlined color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Ecosystem Preferences
                  </Typography>
                </Stack>

                <Stack spacing={1}>
                  {renderFieldLabel('Preferred Ecosystem Types')}
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {selectedTypeLabels.length ? (
                      selectedTypeLabels.map((label) => (
                        <Chip
                          key={label}
                          label={label}
                          sx={{
                            backgroundColor: 'rgba(90, 143, 17, 0.08)',
                            color: 'primary.main',
                            fontWeight: 700,
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No ecosystem types selected.
                      </Typography>
                    )}
                  </Stack>
                </Stack>

                <Stack spacing={1}>
                  {renderFieldLabel('Ecosystem Integrators')}
                  <Stack spacing={1}>
                    {selectedIntegratorLabels.length ? (
                      selectedIntegratorLabels.map((label) => (
                        <Paper
                          key={label}
                          variant="outlined"
                          sx={{
                            px: 2,
                            py: 1.5,
                            borderRadius: 1.5,
                            borderColor: '#E4E6F1',
                            backgroundColor: '#FBFCFE',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {label}
                          </Typography>
                        </Paper>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        None selected
                      </Typography>
                    )}
                  </Stack>
                </Stack>

                <Stack spacing={1}>
                  {renderFieldLabel('Selected Initiatives')}
                  <Stack spacing={1}>
                    {selectedInitiativeLabels.length ? (
                      selectedInitiativeLabels.map((label) => (
                        <Paper
                          key={label}
                          variant="outlined"
                          sx={{
                            px: 2,
                            py: 1.5,
                            borderRadius: 1.5,
                            borderColor: '#E4E6F1',
                            backgroundColor: '#FBFCFE',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {label}
                          </Typography>
                        </Paper>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No initiatives selected.
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                ...sectionPaperStyles,
                background:
                  'linear-gradient(180deg, rgba(248, 251, 242, 1) 0%, rgba(255, 255, 255, 1) 100%)',
              }}
            >
              <Stack spacing={2.25} sx={{ height: '100%' }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <ReceiptLongRounded color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Subscription Summary
                  </Typography>
                </Stack>

                <Paper
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 2,
                    borderRadius: 2,
                    border: '1px solid rgba(90, 143, 17, 0.14)',
                    backgroundColor: 'rgba(90, 143, 17, 0.05)',
                  }}
                >
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 800 }}>
                      {selectedPlan?.planName || formData.subscriptionPlanName || 'Selected Plan'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formData.subscriptionBillingCycle === 'yearly'
                        ? 'Yearly billing'
                        : formData.financialSupportRequested
                        ? '12-Month billing'
                        : 'Monthly billing'}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 30,
                        lineHeight: 1,
                        fontWeight: 800,
                        color: 'primary.main',
                      }}
                    >
                      {summary.totalFormatted}
                    </Typography>
                  </Stack>
                </Paper>

                <SummaryRow
                  label="Subscription Fee"
                  value={formatCurrency(summary.currency, summary.subscriptionPrice)}
                />
                <SummaryRow
                  label="Setup Fee"
                  value={formatCurrency(summary.currency, summary.setupFee)}
                />
                {summary.coverageAmount > 0 && (
                  <SummaryRow
                    label={`Financial Support Coverage (${formData.financialSupportCoveragePercentage}%)`}
                    value={`-${formatCurrency(summary.currency, summary.coverageAmount)}`}
                  />
                )}
                <SummaryRow
                  label="Discount"
                  value={`-${formatCurrency(summary.currency, summary.discountAmount)}`}
                />
                {formData.promoCode ? (
                  <SummaryRow label="Promo Code" value={formData.promoCode} />
                ) : null}
                <SummaryRow
                  label="Financial Support"
                  value={formData.financialSupportRequested ? 'Requested' : 'Not requested'}
                />
                {formData.financialSupportRequested && formData.financialSupportPlanName ? (
                  <SummaryRow
                    label="Support Plan"
                    value={formData.financialSupportPlanName}
                  />
                ) : null}
                {formData.financialSupportRequested && formData.financialSupportProviderName ? (
                  <SummaryRow
                    label="Support Provider"
                    value={formData.financialSupportProviderName}
                  />
                ) : null}
                <Box
                  sx={{
                    mt: 'auto',
                    px: 2,
                    py: 1.35,
                    borderRadius: 999,
                    background:
                      'linear-gradient(90deg, rgba(207, 232, 168, 0.85) 0%, rgba(223, 241, 196, 0.95) 100%)',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      TOTAL{formData.financialSupportRequested ? '*' : ''}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      {summary.totalFormatted}
                    </Typography>
                  </Stack>
                </Box>
                {formData.financialSupportRequested && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    *rest of the amount will be adjusted against the loan amount
                  </Typography>
                )}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={sectionPaperStyles}>
              <Stack spacing={2} sx={{ height: '100%' }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <CreditCardRounded color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Payment Method
                  </Typography>
                </Stack>
                <SummaryRow label="Selected Method" value={paymentMethodLabel} />
                {paymentMethodType === 'stripe' && formData.cardLast4 ? (
                  <>
                    <SummaryRow
                      label="Card"
                      value={`${(formData.cardBrand || 'Card').charAt(0).toUpperCase()}${(formData.cardBrand || 'card').slice(1)} •••• ${formData.cardLast4}`}
                    />
                    <SummaryRow label="Expiry" value={formData.cardExpiry || '-'} />
                  </>
                ) : null}
                {maskedCardLabel && paymentMethodType !== 'stripe' ? (
                  <SummaryRow label="Payment Details" value={maskedCardLabel} />
                ) : null}
                <Typography variant="body2" color="text.secondary">
                  {paymentFlowMode === 'mock'
                    ? 'Payment authorization will be simulated when you complete your enrollment.'
                    : 'Your card will be charged when you complete your enrollment.'}
                </Typography>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={sectionPaperStyles}>
              <Stack spacing={2} sx={{ height: '100%' }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <CheckCircleOutlineRounded color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Final Confirmation
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Double-check your details before completing your enrollment. After this step,
                  your subscription request will be submitted using your selected payment
                  method.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You can still go back to edit any previous step if something needs to
                  change.
                </Typography>
              </Stack>
            </Paper>
          </Box>

          <Divider flexItem />

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  name="reviewConfirmed"
                  checked={Boolean(formData.reviewConfirmed)}
                  onChange={(event) => {
                    handleReviewConfirmationChange(event);
                  }}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  I reviewed all details, understand the selected subscription and
                  payment method, and I am ready to enroll.
                </Typography>
              }
              sx={{
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'center',
                m: 0,
                maxWidth: 760,
              }}
            />
          </Box>

          {errors.reviewConfirmed ? (
            <Typography variant="caption" sx={{ color: 'error.main', mt: -2 }}>
              {errors.reviewConfirmed}
            </Typography>
          ) : null}

          {paymentError ? <Alert severity="error">{paymentError}</Alert> : null}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              size="large"
              onClick={handleBack}
              disabled={submitting}
              sx={{
                order: { xs: 2, sm: 1 },
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(90, 143, 17, 0.06)',
                },
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={handleCompleteEnrollment}
              disabled={submitting || !formData.reviewConfirmed}
              startIcon={<CheckCircleOutlineRounded />}
              sx={{ order: { xs: 1, sm: 2 } }}
            >
              {submitting
                ? formData.isCustomPlanRequest
                  ? 'Submitting Request...'
                  : 'Completing Enrollment...'
                : formData.isCustomPlanRequest
                  ? 'Submit Request'
                  : 'Complete Enrollment'}
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* 3D Secure Authentication Dialog */}
      <Dialog
        open={show3DS}
        onClose={() => !is3DSProcessing && setShow3DS(false)}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={is3DSProcessing}
      >
        <DialogTitle>3D Secure Verification</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 2 }}>
            {!is3DSProcessing ? (
              <>
                <Typography color="text.secondary">
                  Your card issuer requires additional verification. We're initiating the secure authentication process.
                </Typography>
                <Alert severity="info">
                  Your bank's authentication modal will appear in a moment. Please complete the verification in the popup or new window.
                </Alert>
              </>
            ) : (
              <>
                <Typography color="text.secondary">
                  Authenticating with your bank...
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Please wait while we communicate with your bank to verify this transaction.
                </Typography>
              </>
            )}

            {paymentError && (
              <Alert severity="error">{paymentError}</Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>

            <Typography variant="caption" color="text.secondary" align="center">
              This process is secure and required by your bank for this transaction. Do not close this window.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => !is3DSProcessing && setShow3DS(false)}
            disabled={is3DSProcessing}
          >
            {is3DSProcessing ? 'Processing...' : 'Cancel'}
          </Button>
          {!is3DSProcessing && (
            <Button
              variant="contained"
              onClick={handle3DSConfirmation}
              disabled={is3DSProcessing}
            >
              Start Verification
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
